import { useMemo, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import StylishNewInput from 'components/DesignSystems/New/StylishNewInput';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import { SharedIcon } from 'components/SharedIcon/SharedIcon';
import { useActualIncidentId } from 'components/IAP/hooks/commonHooks';
import {
  INPUT_STYLE,
  OnAddLayer,
} from 'components/MapPage/CorporateViewTabs/LayersTab/utils/constants';
import { AuthenticatedPortalItems } from 'components/MapPage/CorporateViewTabs/LayersTab/AuthenticatedPortalItems';
import { useCreateManyExternalLayersMutation } from 'hooks/externalLayer/useCreateManyExternalLayersMutation';
import { useCreateInternalLayerMutation } from 'hooks/internalLayer/useCreateInternalLayerMutation';
import type { ScopedPortalItem } from 'components/MapPage/CorporateViewTabs/LayersTab/arcgis-integration/arcGisPortal';
import { LayerFeaturesEditor } from './LayerFeaturesEditor';
import { useUscgMapContext } from './UscgMapContext';
import { useIsCommandCenter } from './utils/isCommandCenter';
import { IncidentSelect } from './IncidentSelect';
import type { LocationInput } from 'types/locations';
import {
  selectGroup,
  selectUserGuid,
  useAppSelector,
} from 'slices/commonSelectors';
import { InternalFeatureList } from './InternalFeatureList';
import { CategorySelect } from './CategorySelect';
import { AddDhsLayer } from './AddDhsLayer';
import { Row, Col } from 'react-bootstrap';

type PendingObject = {
  pendingGraphicId: string;
  location: LocationInput;
};

type AddLayerPanelProps = {
  onBack: () => void;
  layerType: Parameters<OnAddLayer>[0];
};

export function AddLayerPanel({ onBack, layerType }: AddLayerPanelProps) {
  const group = useAppSelector(selectGroup);
  const userGuid = useAppSelector(selectUserGuid);

  const createManyExternalLayers = useCreateManyExternalLayersMutation();
  const createInternalLayer = useCreateInternalLayerMutation();
  const isCommandCenter = useIsCommandCenter();
  const actualIncidentId = useActualIncidentId();
  const [incidentId, setIncidentId] = useState<string | null>(null);
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [selectedItems, setSelectedItems] = useState<
    Map<string, ScopedPortalItem>
  >(new Map());

  const [pendingObjects, setPendingObjects] = useState<PendingObject[]>([]);
  const {
    zoomToLocation,
    stopDrawing,
    clearPendingGraphics,
    removePendingGraphicsByPendingGraphicId,
    setPreviewLayers,
  } = useUscgMapContext();

  const handleBack = () => {
    stopDrawing();
    clearPendingGraphics();
    onBack();
  };

  const discardLatestPendingObject = () => {
    if (pendingObjects.length === 0) return;

    const last = pendingObjects[pendingObjects.length - 1];
    removePendingGraphicsByPendingGraphicId(last.pendingGraphicId);
    setPendingObjects((prev) => prev.slice(0, -1));
  };

  const internalLayerForm = useForm({
    defaultValues: {
      name: '',
      description: '',
      incidentId: isCommandCenter ? '' : actualIncidentId,
    },
  });

  const internalLayerName = useWatch({
    control: internalLayerForm.control,
    name: 'name',
    defaultValue: '',
  });

  const canCreateInternalLayer =
    !!internalLayerName?.trim() && !createInternalLayer.isPending;

  const selectedItemIds = useMemo(
    () => new Set(selectedItems.keys()),
    [selectedItems]
  );

  const isImporting = createManyExternalLayers.isPending;
  const canImport = selectedItemIds.size > 0 && !isImporting;
  const groupGuid = group?.group_guid;

  const handleToggleItem = (item: ScopedPortalItem, checked: boolean) => {
    setSelectedItems((prev) => {
      const next = new Map(prev);
      if (checked) {
        next.set(item.id!, item);
      } else {
        next.delete(item.id!);
      }
      return next;
    });
  };

  const handleToggleAllItems = (
    items: ScopedPortalItem[],
    checked: boolean
  ) => {
    setSelectedItems((prev) => {
      const next = new Map(prev);
      for (const item of items) {
        if (checked) {
          next.set(item.id!, item);
        } else {
          next.delete(item.id!);
        }
      }
      return next;
    });
  };

  const handleImport = async () => {
    const itemsToImport = Array.from(selectedItems.values());

    const effectiveIncidentId = isCommandCenter ? incidentId : actualIncidentId;

    await createManyExternalLayers.mutateAsync({
      groupGuid,
      targetFolderId: selectedFolderId ?? null,
      layers: itemsToImport.map((item) => ({
        incidentId: effectiveIncidentId || null,
        arcgisItemId: item.id,
        serviceUrl: item.url,
        itemType: item.type,
        name: item.title ?? 'Untitled item',
        description: null,
        userGuid: item.userScoped ? userGuid : null,
      })),
    });

    setPreviewLayers([]);
    setSelectedItems(new Map());
    setSelectedFolderId(null);
    handleBack();
  };

  const handleCreateInternalLayer = internalLayerForm.handleSubmit(
    async (data) => {
      if (!groupGuid) return;

      const name = data.name.trim();
      if (!name) {
        internalLayerForm.setError('name', { message: 'Name is required.' });
        return;
      }

      const effectiveIncidentId = isCommandCenter
        ? data.incidentId
        : actualIncidentId;

      console.log('pendingObjects', pendingObjects);

      await createInternalLayer.mutateAsync({
        groupGuid,
        incidentId: effectiveIncidentId || null,
        name,
        description: data.description.trim() || null,
        icsIncidentLocations: pendingObjects.map(({ location }) => ({
          name: location.name,
          description: location.description,
          geometry: location.geometry,
          resourceType: location.resourceType,
          properties: location.properties,
        })),
      });

      clearPendingGraphics();
      stopDrawing();
      internalLayerForm.reset();
      onBack();
    }
  );

  const headerText = (() => {
    if (layerType === 'external') return 'Add layer (external)';
    if (layerType === 'internal') return 'Add layer (internal)';
    if (layerType === 'dhs') return 'Add layer (DHS)';
  })();

  return (
    <div className="d-flex flex-column gap-3 p-2">
      <div className="d-flex align-items-center gap-2">
        <StylishNewButton
          className="button button--link text-white p-0"
          onClick={handleBack}
          aria-label="Back to layers"
        >
          <SharedIcon iconName="arrow_back" classes="me-2" />
        </StylishNewButton>
        <h5 className="my-0 fw-semibold">{headerText}</h5>
      </div>
      <div style={{ marginLeft: '30px' }}>
        {layerType === 'external' && (
          <AuthenticatedPortalItems
            selectedItemIds={selectedItemIds}
            onToggleItem={handleToggleItem}
            onToggleAllItems={handleToggleAllItems}
          />
        )}
        {layerType === 'dhs' && (
          <AddDhsLayer
            selectedItemIds={selectedItemIds}
            onToggleItem={handleToggleItem}
            onToggleAllItems={handleToggleAllItems}
          />
        )}
        {['dhs', 'external'].includes(layerType) && (
          <div className="d-flex flex-column gap-3 mt-3">
            <Row>
              <Col>
                <IncidentSelect
                  value={isCommandCenter ? incidentId : actualIncidentId}
                  onChange={setIncidentId}
                  isDisabled={!isCommandCenter}
                  isClearable
                />
              </Col>
              <Col>
                <CategorySelect
                  label="Category"
                  value={selectedFolderId}
                  onChange={setSelectedFolderId}
                />
              </Col>
            </Row>
            <StylishNewButton
              type="button"
              className="button--primary"
              onClick={handleImport}
              disabled={!canImport}
            >
              {isImporting ? 'Importing…' : 'Import'}
            </StylishNewButton>
          </div>
        )}
      </div>

      {layerType === 'internal' && (
        <>
          <form
            id="add-internal-layer-form"
            onSubmit={handleCreateInternalLayer}
          >
            <div className="d-flex flex-column gap-4">
              {internalLayerForm.formState.errors.name && (
                <div className="text-error">
                  {internalLayerForm.formState.errors.name.message}
                </div>
              )}

              <div>
                <div className="fw-bold mb-2">Name *</div>
                <StylishNewInput
                  {...internalLayerForm.register('name', {
                    required: 'Name is required.',
                  })}
                  style={INPUT_STYLE}
                />

                <div className="fw-bold mb-2 mt-3">Description</div>
                <textarea
                  {...internalLayerForm.register('description')}
                  className="form-control"
                  placeholder="Layer description"
                  rows={4}
                  style={INPUT_STYLE}
                />

                <Controller
                  control={internalLayerForm.control}
                  name="incidentId"
                  render={({ field }) => (
                    <IncidentSelect
                      label="Incident"
                      value={isCommandCenter ? field.value : actualIncidentId}
                      onChange={field.onChange}
                      classes="mt-3"
                      isDisabled={!isCommandCenter}
                    />
                  )}
                />
              </div>
            </div>
          </form>
          <div className="mt-4">
            <LayerFeaturesEditor
              showAddObjectButton={true}
              showSearch={false}
              onSubmitLocation={async ({ location, pendingGraphicId }) => {
                if (!pendingGraphicId) return;
                setPendingObjects((prev) => [
                  ...prev,
                  { pendingGraphicId, location },
                ]);
              }}
              onCancelLocation={discardLatestPendingObject}
            />
            <div className="mt-3">
              <InternalFeatureList
                features={pendingObjects.map((feature, index) => ({
                  key: String(index),
                  featureId: feature.pendingGraphicId,
                  title: feature.location.name,
                  resourceType: feature.location.resourceType,
                  properties: feature.location.properties,
                  updatedAt: null,
                  geometry: feature.location.geometry,
                }))}
                onDelete={(featureId) => {
                  removePendingGraphicsByPendingGraphicId(featureId);
                  setPendingObjects((prev) =>
                    prev.filter((p) => p.pendingGraphicId !== featureId)
                  );
                }}
                onZoomToLocation={zoomToLocation}
              />
            </div>
          </div>
          <div className="d-flex gap-2 mt-3">
            <StylishNewButton
              type="submit"
              form="add-internal-layer-form"
              className="button--primary"
              disabled={!canCreateInternalLayer}
            >
              {createInternalLayer.isPending ? 'Creating…' : 'Create'}
            </StylishNewButton>
          </div>
        </>
      )}
    </div>
  );
}
